<!--
  src/components/DialogPlaceInPlan.vue
  Stage 2 (bulk) — place already-linked course tasks (memberships) into a plan on a date.
  Opened from the memberships tab's row action. Each selected row carries `tasksBankCourseId`
  (the membership id), which is what the plan is placed with.
  One batched call — POST api/Admin/StructuredPlan/PlanTasks:
    { CourseId, ProgramTypeId, Tasks: [{ TasksBankCourseId, TaskDate, StartTime?, EndTime?, TimeWindow? }] }
-->
<template>
  <div class="DialogPlaceInPlan" dir="rtl">
    <div class="modal-card" style="width: 460px;">
      <header class="modal-card-head">
        <p class="modal-card-title">שיבוץ משימות בתכנית</p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body">
        <p class="DialogPlaceInPlan__Count">
          נבחרו <b>{{ tasks.length }}</b> משימות לשיבוץ, סה״כ זמן <b>{{ totalTime }}</b>.
        </p>

        <b-field label="סוג תוכנית">
          <b-select v-model="programTypeId" placeholder="בחר סוג תוכנית" expanded>
            <option v-for="p in programTypes" :key="p.Value" :value="p.Value">{{ p.Text }}</option>
          </b-select>
        </b-field>

        <b-field label="תאריך">
          <b-datepicker v-model="date" icon="calendar-today" placeholder="בחר תאריך" trap-focus editable />
        </b-field>

        <b-field label="חלון זמן">
          <div class="time-window">
            <button v-for="w in timeWindows" :key="w.Value" type="button"
              :class="{ on: timeWindow === w.Value }"
              :style="timeWindow === w.Value ? { background: w.color, borderColor: w.color, color: '#fff' } : {}"
              @click="timeWindow = w.Value">
              <span class="dot" :style="{ background: w.color }"></span>{{ w.Text }}
            </button>
          </div>
        </b-field>

        <div class="note">כל משימה שכבר שובצה בתכנית זו תדולג.</div>
      </section>

      <footer class="modal-card-foot">
        <b-button :loading="isLoading" :disabled="programTypeId == null" type="is-primary" label="שבץ" @click="save" />
        <b-button :disabled="isLoading" label="ביטול" @click="$emit('close')" />
      </footer>
    </div>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from "buefy";
import TasksService from "@/services/TasksService";
import TaskDuration from "@/services/TaskDuration";
import moment from "moment";

const TIME_WINDOWS = [
  { Value: "any", Text: "כל היום", color: "#00427e" },
  { Value: "morning", Text: "בוקר", color: "#00afd8" },
  { Value: "evening", Text: "ערב", color: "#E1583D" },
];

export default {
  name: "DialogPlaceInPlan",
  props: {
    courseId: { type: [Number, String], required: true },
    tasks: { type: Array, default: () => [] },  // flattened membership rows (carry tasksBankCourseId)
    defaultProgramTypeId: { type: [Number, String], default: null },
    onSuccess: { type: Function, default: null },
  },
  data() {
    return {
      isLoading: false,
      programTypes: [],
      timeWindows: TIME_WINDOWS,
      programTypeId: this.defaultProgramTypeId != null ? this.defaultProgramTypeId : null,
      date: new Date(),
      timeWindow: "any",
    };
  },
  computed: {
    totalTime() { return TaskDuration.toHours(TaskDuration.totalMinutes(this.tasks)); },
  },
  created() {
    TasksService.getProgramTypes().then((r) => {
      this.programTypes = (r.data || []).map((i) => ({ Value: i.id, Text: i.name }));
      if (this.programTypeId == null && this.programTypes.length) {
        this.programTypeId = this.programTypes[0].Value;
      }
    });
  },
  methods: {
    save() {
      if (this.programTypeId == null || !this.tasks.length) return;
      this.isLoading = true;
      const taskDate = this.date ? moment(this.date).format("YYYY-MM-DD") : null;
      TasksService.addStructuredPlanTask({
        CourseId: this.courseId,
        ProgramTypeId: this.programTypeId,
        Tasks: this.tasks.map((t) => ({
          TasksBankCourseId: t.tasksBankCourseId,
          TaskDate: taskDate,
          TimeWindow: this.timeWindow,
        })),
      })
        .then(() => {
          Toast.open({ type: "is-success", message: `${this.tasks.length} משימות שובצו בתכנית!`, duration: 4000 });
          if (this.onSuccess) this.onSuccess();
          this.$emit("close");
        })
        .catch((error) => {
          const serverMsg = error && error.response && error.response.data
            && (error.response.data.Error || error.response.data.error);
          Toast.open({ type: "is-danger", message: serverMsg || "השיבוץ נכשל!", duration: 8000 });
        })
        .finally(() => { this.isLoading = false; });
    },
  },
};
</script>

<style scoped lang="scss">
.DialogPlaceInPlan__Count {
  margin-bottom: 14px;
}
.time-window {
  display: flex;
  gap: 8px;
  width: 100%;
  button {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 10px 0;
    border: 1px solid #cbd6e2;
    border-radius: 6px;
    background: #fff;
    color: #43535f;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    .dot { width: 9px; height: 9px; border-radius: 50%; }
    &.on .dot { display: none; }
  }
}
.note {
  margin-top: 16px;
  font-size: 13px;
  color: #6b7b8c;
}
</style>
